/**
 * Turn the Portal's inline base64 asset uploads into real bundled files.
 *
 * The Builder stores uploaded logos / wallpapers / tile icons inline as
 * `*DataUrl` fields on the config (browser-side, no asset host needed).
 * That's convenient for authoring but bloats config.json — every device
 * re-parses the base64 on every boot/poll. At bundle time we externalize
 * each inline data URL into an `assets/<file>` entry in the ZIP and
 * rewrite the config to reference it by `*Ref` (the renderer resolves
 * `<ref>` to `/assets/<ref>`), dropping the inline copy.
 *
 * Inline fields handled (mirrors the schema):
 *   - profile.theme.logoDataUrl       → logoRef
 *   - profile.theme.wallpaperDataUrl  → wallpaperRef
 *   - tile.iconDataUrl                → iconRef   (grid tiles incl. folder
 *                                                  children, tools, featuredTiles)
 *
 * Pure: operates on a deep copy and returns the rewritten config plus the
 * asset list. Identical data URLs are de-duplicated to a single file.
 * Anything that isn't a parseable `data:*;base64,` string is left
 * untouched.
 */

export interface ExternalizedAsset {
  /** ZIP path, e.g. `assets/logo-<profileId>.png`. */
  path: string;
  /** Base64 payload (the part after `;base64,`), for `JSZip.file(_, _, { base64: true })`. */
  base64: string;
}

export interface ExternalizeResult {
  config: unknown;
  assets: ExternalizedAsset[];
}

const MIME_EXT: Readonly<Record<string, string>> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/svg+xml': 'svg',
};

function parseDataUrl(value: string): { mime: string; base64: string } | null {
  const match = /^data:([^;,]+);base64,(.*)$/s.exec(value);
  if (match === null) return null;
  const [, mime, base64] = match;
  if (mime === undefined || base64 === undefined) return null;
  return { mime, base64 };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

export function externalizeInlineAssets(config: unknown): ExternalizeResult {
  if (!isRecord(config)) return { config, assets: [] };

  const root = JSON.parse(JSON.stringify(config)) as Record<string, unknown>;
  const assets: ExternalizedAsset[] = [];
  const seen = new Map<string, string>(); // dataUrl → ref filename

  function register(dataUrl: string, hint: string): string | null {
    const cached = seen.get(dataUrl);
    if (cached !== undefined) return cached;
    const parsed = parseDataUrl(dataUrl);
    if (parsed === null) return null;
    const ext = MIME_EXT[parsed.mime.toLowerCase()] ?? 'bin';
    const ref = `${hint}.${ext}`;
    assets.push({ path: `assets/${ref}`, base64: parsed.base64 });
    seen.set(dataUrl, ref);
    return ref;
  }

  /** Move an inline data-URL field to its `*Ref` sibling, registering the asset. */
  function move(obj: Record<string, unknown>, fromKey: string, toKey: string, hint: string): void {
    const value = obj[fromKey];
    if (typeof value !== 'string') return;
    const ref = register(value, hint);
    if (ref === null) return; // not a parseable data URL — leave as-is
    obj[toKey] = ref;
    delete obj[fromKey];
  }

  function walkTile(tile: unknown): void {
    if (!isRecord(tile)) return;
    const id = typeof tile.id === 'string' ? tile.id : 'tile';
    move(tile, 'iconDataUrl', 'iconRef', `tile-${id}`);
    if (tile.type === 'folder' && Array.isArray(tile.children)) {
      for (const child of tile.children) walkTile(child);
    }
  }

  const profiles = Array.isArray(root.profiles) ? root.profiles : [];
  for (const p of profiles) {
    if (!isRecord(p)) continue;
    const pid = typeof p.id === 'string' ? p.id : 'profile';

    if (isRecord(p.theme)) {
      move(p.theme, 'logoDataUrl', 'logoRef', `logo-${pid}`);
      move(p.theme, 'wallpaperDataUrl', 'wallpaperRef', `wallpaper-${pid}`);
    }

    if (isRecord(p.grid) && Array.isArray(p.grid.tiles)) {
      for (const tile of p.grid.tiles) walkTile(tile);
    }
    if (Array.isArray(p.tools)) {
      for (const tile of p.tools) walkTile(tile);
    }
    if (Array.isArray(p.featuredTiles)) {
      for (const tile of p.featuredTiles) walkTile(tile);
    }
  }

  return { config: root, assets };
}
